/**
 * useFormCloseAndCreate
 *
 * Owns both routes that raise the next form from the active one: the explicit
 * "Close & Create" entry, which asks for a close state and a form type in the
 * drawer, and the "Create new form" entries, which already know their form type.
 * Composed by `FormWorkflowActionProvider` so the menus and the command palette
 * share one drawer and one `isCreating` flag.
 *
 * Invariant (SYD-2816): the source version is never left open behind a next
 * form. A version still on the draft line goes through the drawer so a close
 * state is picked first; a version that is already closed is created from
 * directly, because its state graph need not — and often cannot — reach a second
 * non-draft state.
 *
 * Takes only scalars and form-type config — never document slots — so nothing
 * here subscribes the provider to field values. Form-type short labels are
 * resolved by the drawer when it opens.
 */

import { useCallback, useState } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import { formStateDescription, type FormStateId, type FormType } from '#types';
import FormCloseAndCreateDrawer from '@/components/artifacts/form/FormCloseAndCreateDrawer';
import { setFormState, createFormFromForm } from '@/services/artifactFunctions';
import { formActions } from '@/store';
import { isCloseAndCreateTargetState } from './isCloseAndCreateTargetState';
import { isDraftFormState } from './isDraftFormState';
import { checkRequiredFields } from './checkRequiredFields';

/**
 * The state graph and next-form list as read from a Valtio snapshot: deeply
 * readonly, and only the two members this hook needs.
 */
type FormStateTransitions = Readonly<Record<string, { readonly allowedNextStates: readonly FormStateId[] }>>;

type UseFormCloseAndCreateParams = {
  formId: string;
  versionId: string | undefined;
  versionState: FormStateId | undefined;
  possibleStates: FormStateTransitions | undefined;
  allowedNextForms: readonly FormType['docId'][] | undefined;
  messageApi: MessageInstance;
  isCreating: boolean;
  setIsCreating: (creating: boolean) => void;
};

export const useFormCloseAndCreate = ({
  formId,
  versionId,
  versionState,
  possibleStates,
  allowedNextForms,
  messageApi,
  isCreating,
  setIsCreating,
}: UseFormCloseAndCreateParams) => {
  const [isCloseAndCreateDrawerOpen, setIsCloseAndCreateDrawerOpen] = useState(false);
  const [closeAndCreateState, setCloseAndCreateState] = useState<FormStateId | null>(null);
  const [closeAndCreateFormType, setCloseAndCreateFormType] = useState<string | null>(null);

  const allowedNextStates = versionState
    ? possibleStates?.[versionState]?.allowedNextStates
    : undefined;

  /** Already off the draft line: nothing left to close before the next form. */
  const isVersionClosed = versionState !== undefined && !isDraftFormState(versionState);

  const closeAndCreateAllowedStates = (allowedNextStates ?? []).filter(isCloseAndCreateTargetState);
  const closeAndCreateStateOptions = closeAndCreateAllowedStates.map((state) => ({
    value: state,
    label: formStateDescription[state]?.shortLabel || state,
  }));
  const closeAndCreateFormTypeIds = (allowedNextForms ?? [])
    .filter((formType): formType is string => typeof formType === 'string' && formType.length > 0);

  const onOpenCloseAndCreate = useCallback(() => {
    if (!checkRequiredFields()) return;
    if (closeAndCreateAllowedStates.length === 0 || (allowedNextForms?.length ?? 0) === 0) return;
    setCloseAndCreateState(null);
    setCloseAndCreateFormType(null);
    setIsCloseAndCreateDrawerOpen(true);
  }, [
    closeAndCreateAllowedStates.length,
    allowedNextForms?.length,
  ]);

  /** Closes the source version when a state is given, then creates the linked form. */
  const runCloseAndCreate = useCallback(async (
    closeToState: FormStateId | null,
    newFormType: string,
  ) => {
    if (!versionId) return;
    setIsCreating(true);
    try {
      if (closeToState) await setFormState(formId, versionId, closeToState);
      const result = await createFormFromForm({
        formId,
        sourceVersionId: versionId,
        newFormType,
      });
      if ('newFormId' in result && result.newFormId && result.newVersionId) {
        formActions.navigateToVersion(result.newFormId, result.newVersionId);
      }
      messageApi.success(closeToState
        ? 'Form closed and linked form created.'
        : 'Linked form created.');
      setIsCloseAndCreateDrawerOpen(false);
    } catch (err) {
      const action = closeToState ? 'Close & Create' : 'Create new form';
      messageApi.error(`Error running ${action}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsCreating(false);
    }
  }, [
    formId,
    versionId,
    messageApi,
    setIsCreating,
  ]);

  const handleConfirmCloseAndCreate = useCallback(() => {
    if (!closeAndCreateState || !closeAndCreateFormType) return;
    void runCloseAndCreate(closeAndCreateState, closeAndCreateFormType);
  }, [
    closeAndCreateState,
    closeAndCreateFormType,
    runCloseAndCreate,
  ]);

  const onCreateNextForm = useCallback((newFormType: FormType['docId']) => {
    if (!newFormType) return;
    if (!checkRequiredFields()) return;
    if (isVersionClosed) {
      void runCloseAndCreate(null, newFormType);
      return;
    }
    if (closeAndCreateAllowedStates.length === 0) return;
    setCloseAndCreateState(null);
    setCloseAndCreateFormType(newFormType);
    setIsCloseAndCreateDrawerOpen(true);
  }, [
    isVersionClosed,
    closeAndCreateAllowedStates.length,
    runCloseAndCreate,
  ]);

  const isCloseAndCreateEntryDisabled =
    isCreating
    || closeAndCreateAllowedStates.length === 0
    || (allowedNextForms?.length ?? 0) === 0;

  const isCreateNextFormDisabled =
    isCreating
    || (allowedNextForms?.length ?? 0) === 0
    || (!isVersionClosed && closeAndCreateAllowedStates.length === 0);

  return {
    allowedNextStates,
    allowedNextForms,
    onOpenCloseAndCreate,
    isCloseAndCreateEntryDisabled,
    onCreateNextForm,
    isCreateNextFormDisabled,
    closeAndCreateDrawer: (
      <FormCloseAndCreateDrawer
        open={isCloseAndCreateDrawerOpen}
        confirmLoading={isCreating}
        allowedStateOptions={closeAndCreateStateOptions}
        allowedFormTypeIds={closeAndCreateFormTypeIds}
        selectedState={closeAndCreateState}
        selectedFormType={closeAndCreateFormType}
        onChangeState={setCloseAndCreateState}
        onChangeFormType={setCloseAndCreateFormType}
        onCancel={() => setIsCloseAndCreateDrawerOpen(false)}
        onConfirm={handleConfirmCloseAndCreate}
      />
    ),
  };
};
